import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSubscription } from "@/context/SubscriptionContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle, Crown } from "lucide-react";
import usePageSeo from "@/hooks/usePageSeo";

const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const loc = useLocation();
  const nav = useNavigate();
  const { refresh } = useSubscription();
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const timerRef = useRef(null);
  usePageSeo({
    title: "Payment received — Welcome to DPP Pro",
    description: "Your DPP Pro subscription is active. Unlock the AI Portfolio Optimizer, Income Target Planner, and AI Research Chat, plus unlimited backtests and narratives.",
    path: "/payment/success",
    noindex: true,
  });

  useEffect(() => {
    const params = new URLSearchParams(loc.search);
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }

    let cancelled = false;

    const poll = async (n) => {
      if (cancelled) return;
      setAttempt(n + 1);
      try {
        const res = await api.get(`/payments/status/${sid}`);
        if (res.data.payment_status === "paid") {
          setState("paid");
          refresh();
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
      } catch (err) {
        // 404 means the session_id is unknown — no point polling further.
        if (err?.response?.status === 404) { setState("error"); return; }
        // other errors are transient — keep polling
      }
      if (n + 1 >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      timerRef.current = setTimeout(() => poll(n + 1), 2000);
    };
    poll(0);
    return () => { cancelled = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [loc.search, refresh]);

  return (
    <div className="max-w-2xl mx-auto px-6 py-20">
      <Card>
        <CardContent className="p-10 text-center">
          {state === "polling" && (
            <>
              <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
              <h1 className="serif text-3xl mt-4">Confirming your payment…</h1>
              <p className="text-muted-foreground mt-2 text-sm">
                Talking to Stripe (attempt {attempt}/{MAX_ATTEMPTS}).
              </p>
            </>
          )}
          {state === "paid" && (
            <>
              <div className="w-14 h-14 rounded-full bg-primary/10 grid place-items-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-primary" />
              </div>
              <h1 className="serif text-4xl mt-4">Welcome to Pro <Crown className="inline w-6 h-6 text-secondary ml-1" /></h1>
              <p className="text-muted-foreground mt-3">
                Unlimited portfolios and unlimited AI explanations are unlocked. Enjoy.
              </p>
              <div className="mt-8 flex gap-2 justify-center">
                <Button className="rounded-full" onClick={() => nav("/dashboard")}>Open dashboard</Button>
                <Button variant="outline" className="rounded-full" onClick={() => nav("/portfolio/new")}>New portfolio</Button>
              </div>
            </>
          )}
          {state === "timeout" && (
            <>
              <Loader2 className="w-10 h-10 text-muted-foreground mx-auto" />
              <h1 className="serif text-3xl mt-4">Still processing…</h1>
              <p className="text-muted-foreground mt-2 text-sm">
                This can happen with certain payment methods. Check back in a minute.
              </p>
              <Button className="mt-6 rounded-full" onClick={() => nav("/pricing")}>Back to pricing</Button>
            </>
          )}
          {state === "error" && (
            <>
              <div className="w-14 h-14 rounded-full bg-destructive/10 grid place-items-center mx-auto">
                <XCircle className="w-8 h-8 text-destructive" />
              </div>
              <h1 className="serif text-3xl mt-4">Something went wrong</h1>
              <p className="text-muted-foreground mt-2 text-sm">We couldn&apos;t confirm the payment. Please try again.</p>
              <Button className="mt-6 rounded-full" onClick={() => nav("/pricing")}>Back to pricing</Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
